import React from "react";
import PropTypes from "prop-types";
import CommonFilterInput from "./CommonFilterInput";
import "./CommonFilters.css";
import { useLocation } from "react-router-dom";

const CommonFilters = ({
  members = [],
  carriers,
  groups,
  assignees,
  providers,
  pharmacies,
  programs,
  statuses = ["Complete", "Incomplete"],
  drugLists = [],
  presets = ["Unassigned", "Zero Contact", "Monitoring"],
  memberFilter = [],
  carrierFilter = [],
  groupFilter = [],
  assigneeFilter = [],
  providerFilter = [],
  pharmacyFilter = [],
  dispositionFilter = [],
  druglistFilter = [],
  programFilter = [],
  statusFilter = [],
  onMemberChange,
  onProviderChange,
  onPharmacyChange,
  onAssigneeChange,
  onCarrierChange,
  onGroupChange,
  onProgramChange,
  onDispositionChange,
  onDruglistChange,
  onSearch,
  isPharmacy = false,
  isProvider = false,
  onOpenDropdown,
  onLoadMoreOptions,
  loadingMap = {},
  hasMoreMap = {},
  autoPrefetch = true,
  compact = false,

  // Search value props
  programSearch = "",
  carrierSearch = "",
  groupSearch = "",
  assigneeSearch = "",
  visibleFilters = ["STATUS", "CARRIER", "GROUP", "PROGRAM", "ASSIGNEE"],
  showLabel = true,

  // Recent activity props
  recentActivityOutcomes = [],
  recentActivityNestedOptions = {},
  recentActivityOutcomeFilter = [],
  recentActivityReasonFilter = [],
  onRecentActivityChange,
}) => {
  const location = useLocation();

  const isAllMembers =
    location.pathname.includes("all-members") ||
    location.pathname.includes("priority-outreach") ||
    location.pathname.includes("monitoring");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;

  const assigneeOptions = getAssigneeOptions(assignees);

  return (
    <div className="common-filters">
      {!isPharmacy && !isProvider && (
        <StandardFilters
          compact={compact}
          dispositionFilter={dispositionFilter}
          onDispositionChange={onDispositionChange}
          druglistFilter={druglistFilter}
          onDruglistChange={onDruglistChange}
          statuses={statuses}
          drugLists={drugLists}
          onSearch={onSearch}
          isAllMembers={isAllMembers}
          onOpenDropdown={onOpenDropdown}
          onLoadMoreOptions={onLoadMoreOptions}
          loadingMap={loadingMap}
          hasMoreMap={hasMoreMap}
          autoPrefetch={autoPrefetch}
          role={role}
          carrierFilter={carrierFilter}
          onCarrierChange={onCarrierChange}
          carriers={carriers}
          carrierSearch={carrierSearch}
          groupFilter={groupFilter}
          onGroupChange={onGroupChange}
          groups={groups}
          groupSearch={groupSearch}
          assigneeFilter={assigneeFilter}
          onAssigneeChange={onAssigneeChange}
          assigneeOptions={assigneeOptions}
          assigneeSearch={assigneeSearch}
          visibleFilters={visibleFilters}
          showLabel={showLabel}
          recentActivityOutcomes={recentActivityOutcomes}
          recentActivityNestedOptions={recentActivityNestedOptions}
          recentActivityOutcomeFilter={recentActivityOutcomeFilter}
          recentActivityReasonFilter={recentActivityReasonFilter}
          onRecentActivityChange={onRecentActivityChange}
        />
      )}
    </div>
  );
};

const getAssigneeOptions = (assignees) => {
  // Ensure 'Unassigned' is always present below 'All' in Assignee filter
  let assigneeOptions = Array.isArray(assignees) ? [...assignees] : [];
  if (!assigneeOptions.includes("Unassigned")) {
    // Insert 'Unassigned' after 'All' if 'All' exists, else at the top
    const allIdx = assigneeOptions.findIndex(
      (opt) => opt.toLowerCase() === "all",
    );
    if (allIdx === -1) {
      assigneeOptions.unshift("Unassigned");
    } else {
      assigneeOptions.splice(allIdx + 1, 0, "Unassigned");
    }
  }
  return assigneeOptions;
};

const StandardFilters = ({
  compact,
  dispositionFilter,
  onDispositionChange,
  druglistFilter,
  onDruglistChange,
  statuses,
  drugLists,
  onSearch,
  isAllMembers,
  onOpenDropdown,
  onLoadMoreOptions,
  loadingMap,
  hasMoreMap,
  autoPrefetch,
  role,
  carrierFilter,
  onCarrierChange,
  carriers,
  carrierSearch,
  groupFilter,
  onGroupChange,
  groups,
  groupSearch,
  assigneeFilter,
  onAssigneeChange,
  assigneeOptions,
  assigneeSearch,
  visibleFilters,
  showLabel,
  recentActivityOutcomes,
  recentActivityNestedOptions,
  recentActivityOutcomeFilter,
  recentActivityReasonFilter,
  onRecentActivityChange,
}) => {
  return (
    <>
      {visibleFilters.includes("STATUS") && (
        <CommonFilterInput
          label="Disposition"
          placeholder={compact ? "Disposition" : "Filter by Disposition"}
          value={dispositionFilter}
          onChange={onDispositionChange}
          options={statuses}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="STATUS"
          isAllMembers={isAllMembers}
          onOpen={onOpenDropdown}
          onLoadMore={onLoadMoreOptions}
          isLoading={loadingMap.STATUS}
          hasMore={hasMoreMap.STATUS}
          autoPrefetch={autoPrefetch}
          compact={compact}
          showLabel={showLabel}
        />
      )}
      {visibleFilters.includes("CARRIER") && role !== "L8" && (
        <CommonFilterInput
          label="Carrier"
          placeholder={compact ? "Carrier" : "Filter by Carrier"}
          value={carrierFilter}
          onChange={onCarrierChange}
          options={Array.isArray(carriers) ? carriers : []}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="CARRIER"
          onOpen={onOpenDropdown}
          isLoading={loadingMap.CARRIER}
          autoPrefetch={autoPrefetch}
          compact={compact}
          searchValue={carrierSearch}
          showLabel={showLabel}
        />
      )}
      {visibleFilters.includes("GROUP") && role !== "L8" && (
        <CommonFilterInput
          label="Group"
          placeholder={compact ? "Group" : "Filter by Group"}
          value={groupFilter}
          onChange={onGroupChange}
          options={Array.isArray(groups) ? groups : []}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="GROUP"
          onOpen={onOpenDropdown}
          isLoading={loadingMap.GROUP}
          autoPrefetch={autoPrefetch}
          compact={compact}
          searchValue={groupSearch}
          showLabel={showLabel}
        />
      )}
      {visibleFilters.includes("DRUG_LIST") && (
        <CommonFilterInput
          label="Drug List"
          placeholder={compact ? "Filter by Drug List" : "Drug List"}
          value={druglistFilter}
          onChange={onDruglistChange}
          options={drugLists}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="DRUG_LIST"
          onOpen={onOpenDropdown}
          onLoadMore={onLoadMoreOptions}
          isLoading={loadingMap.DRUG_LIST}
          hasMore={hasMoreMap.DRUG_LIST}
          autoPrefetch={autoPrefetch}
          compact={compact}
          showLabel={showLabel}
        />
      )}
      {visibleFilters.includes("ASSIGNEE") && (
        <CommonFilterInput
          label="Assignee"
          placeholder={compact ? "Assignee" : "Filter by Assignee"}
          value={assigneeFilter}
          onChange={onAssigneeChange}
          options={assigneeOptions}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="ASSIGNEE"
          onOpen={onOpenDropdown}
          isLoading={loadingMap.ASSIGNEE}
          autoPrefetch={autoPrefetch}
          compact={compact}
          searchValue={assigneeSearch}
          showLabel={showLabel}
        />
      )}
      {visibleFilters.includes("RECENT_ACTIVITY") && (
        <CommonFilterInput
          label="Recent Activity"
          placeholder={
            compact ? "Recent Activity" : "Filter by Recent Activity"
          }
          value={recentActivityOutcomeFilter}
          onChange={onRecentActivityChange}
          options={recentActivityOutcomes}
          type="select"
          multiple={true}
          onSearch={onSearch}
          filterType="RECENT_ACTIVITY"
          onOpen={onOpenDropdown}
          isLoading={loadingMap.RECENT_ACTIVITY}
          autoPrefetch={autoPrefetch}
          compact={compact}
          showLabel={showLabel}
          nestedOptions={recentActivityNestedOptions}
          nestedValues={recentActivityReasonFilter}
          onNestedToggle={(parent, child) => {
            const parentReasons = Array.isArray(
              recentActivityNestedOptions?.[parent],
            )
              ? recentActivityNestedOptions[parent]
              : [];
            const nextReasons = recentActivityReasonFilter.includes(child)
              ? recentActivityReasonFilter.filter((r) => r !== child)
              : [...recentActivityReasonFilter, child];
            const hasAnyChildSelected = parentReasons.some((reason) =>
              nextReasons.includes(reason),
            );
            const nextOutcomes = hasAnyChildSelected
              ? Array.from(new Set([...recentActivityOutcomeFilter, parent]))
              : recentActivityOutcomeFilter.filter(
                  (outcome) => outcome !== parent,
                );
            onRecentActivityChange?.(nextOutcomes, nextReasons);
          }}
        />
      )}
    </>
  );
};

StandardFilters.propTypes = {
  compact: PropTypes.bool,
  dispositionFilter: PropTypes.array,
  onDispositionChange: PropTypes.func,
  druglistFilter: PropTypes.array,
  onDruglistChange: PropTypes.func,
  statuses: PropTypes.array,
  drugLists: PropTypes.array,
  onSearch: PropTypes.func,
  isAllMembers: PropTypes.bool,
  onOpenDropdown: PropTypes.func,
  onLoadMoreOptions: PropTypes.func,
  loadingMap: PropTypes.object,
  hasMoreMap: PropTypes.object,
  autoPrefetch: PropTypes.bool,
  role: PropTypes.string,
  carrierFilter: PropTypes.array,
  onCarrierChange: PropTypes.func,
  carriers: PropTypes.array,
  carrierSearch: PropTypes.string,
  groupFilter: PropTypes.array,
  onGroupChange: PropTypes.func,
  groups: PropTypes.array,
  groupSearch: PropTypes.string,
  assigneeFilter: PropTypes.array,
  onAssigneeChange: PropTypes.func,
  assigneeOptions: PropTypes.array,
  assigneeSearch: PropTypes.string,
  visibleFilters: PropTypes.arrayOf(PropTypes.string),
  showLabel: PropTypes.bool,
  recentActivityOutcomes: PropTypes.array,
  recentActivityNestedOptions: PropTypes.object,
  recentActivityOutcomeFilter: PropTypes.array,
  recentActivityReasonFilter: PropTypes.array,
  onRecentActivityChange: PropTypes.func,
};

export default CommonFilters;

CommonFilters.propTypes = {
  members: PropTypes.arrayOf(PropTypes.string),
  carriers: PropTypes.arrayOf(PropTypes.string),
  groups: PropTypes.arrayOf(PropTypes.string),
  assignees: PropTypes.arrayOf(PropTypes.string),
  providers: PropTypes.arrayOf(PropTypes.string),
  pharmacies: PropTypes.arrayOf(PropTypes.string),
  programs: PropTypes.arrayOf(PropTypes.string),
  statuses: PropTypes.arrayOf(PropTypes.string),
  drugLists: PropTypes.arrayOf(PropTypes.string),
  presets: PropTypes.arrayOf(PropTypes.string),
  memberFilter: PropTypes.arrayOf(PropTypes.string),
  carrierFilter: PropTypes.arrayOf(PropTypes.string),
  groupFilter: PropTypes.arrayOf(PropTypes.string),
  assigneeFilter: PropTypes.arrayOf(PropTypes.string),
  providerFilter: PropTypes.arrayOf(PropTypes.string),
  pharmacyFilter: PropTypes.arrayOf(PropTypes.string),
  dispositionFilter: PropTypes.arrayOf(PropTypes.string),
  druglistFilter: PropTypes.arrayOf(PropTypes.string),
  programFilter: PropTypes.arrayOf(PropTypes.string),
  statusFilter: PropTypes.arrayOf(PropTypes.string),
  onMemberChange: PropTypes.func,
  onProviderChange: PropTypes.func,
  onPharmacyChange: PropTypes.func,
  onAssigneeChange: PropTypes.func,
  onCarrierChange: PropTypes.func,
  onGroupChange: PropTypes.func,
  onProgramChange: PropTypes.func,
  onDispositionChange: PropTypes.func,
  onDruglistChange: PropTypes.func,
  onSearch: PropTypes.func,
  isPharmacy: PropTypes.bool,
  isProvider: PropTypes.bool,
  onOpenDropdown: PropTypes.func,
  onLoadMoreOptions: PropTypes.func,
  loadingMap: PropTypes.object,
  hasMoreMap: PropTypes.object,
  autoPrefetch: PropTypes.bool,
  compact: PropTypes.bool,
  programSearch: PropTypes.string,
  carrierSearch: PropTypes.string,
  groupSearch: PropTypes.string,
  assigneeSearch: PropTypes.string,
  visibleFilters: PropTypes.arrayOf(PropTypes.string),
  showLabel: PropTypes.bool,
  recentActivityOutcomes: PropTypes.arrayOf(PropTypes.string),
  recentActivityNestedOptions: PropTypes.object,
  recentActivityOutcomeFilter: PropTypes.arrayOf(PropTypes.string),
  recentActivityReasonFilter: PropTypes.arrayOf(PropTypes.string),
  onRecentActivityChange: PropTypes.func,
};
